// Generator.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ExternalHyperlink } from "docx";
import ReactMarkdown from "react-markdown";
import { humanizeText } from "../components/humanizer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://onecrmapi.oneaihuman.com';

function Generator({ theme }) {
    const [question, setQuestion] = useState("");
    const [chatHistory, setChatHistory] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [typing, setTyping] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [blogData, setBlogData] = useState([]);
    const chatEndRef = useRef(null);

    const [blogProcessLogs, setBlogProcessLogs] = useState([]);
    const addBlogProcessLog = (message) => {
        setBlogProcessLogs((prev) => [...prev, message]);
        console.log("Blog Process Log:", message);
    };

    const [failedBlogs, setFailedBlogs] = useState([]);

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory, typing]);

    const blogProcessLogsEndRef = useRef(null);
    useEffect(() => {
        blogProcessLogsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [blogProcessLogs]);

    const handleNewChat = () => {
        setChatHistory([]);
        setQuestion("");
    };

    const handleExcelFileChange = (event) => {
        const uploadedFile = event.target.files[0];
        if (!uploadedFile) return alert("Please select an Excel file!");
        setExcelFile(uploadedFile);
        addBlogProcessLog(`Excel file selected: ${uploadedFile.name}`);
    };

    const handleImageBatchChange = (event) => {
        const files = Array.from(event.target.files);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        if (files.length !== imageFiles.length) {
            alert("Some selected files were not images and have been ignored.");
        }
        setUploadedImages(imageFiles);
        addBlogProcessLog(`Selected ${imageFiles.length} image(s) for batch upload.`);
    };

    const handleCancelUpload = () => {
        setExcelFile(null);
        setUploadedImages([]);
        setBlogData([]);
        setBlogProcessLogs([]);
        setFailedBlogs([]);
        alert("All uploads and data cleared.");
    };

    const handleFileUpload = async () => {
        if (!excelFile) return alert("Please upload an Excel file first!");

        const reader = new FileReader();
        reader.readAsBinaryString(excelFile);
        reader.onload = (e) => {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: "binary" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet);

            if (rows.length === 0) {
                alert("No valid blog data found in Excel!");
                return;
            }

            const extractedData = rows.map((row) => ({
                topic: row["topic"]?.trim() || "Untitled",
                audience: row["audience"]?.trim() || "General",
                keywords: row["keywords"]?.trim() || "",
                wpSiteUrl: row["WP_SITE_URL"]?.trim() || "",
                wpUsername: row["WP_USERNAME"]?.trim() || "",
                wpAppPassword: row["WP_APP_PASSWORD"]?.trim() || "",
                linkword: row["linkword"]?.trim() || "",
                linkurl: row["linkurl"]?.trim() || "",
                category: row["category"]?.trim() || "Uncategorized",
                tags: row["tags"]?.trim() || "",
                featuredImageFilename: row["featuredImage"]?.trim() || "",
                shortblog: row["shortblog"]?.trim().toLowerCase() || "",
                publishDate: row["publishDate"] !== undefined ? row["publishDate"] : null,
                publishTime: row["publishTime"] !== undefined ? row["publishTime"] : null,
            }));

            setBlogData(extractedData);
            addBlogProcessLog(`Successfully loaded ${extractedData.length} entries from Excel.`);
            alert("Excel file uploaded successfully! Now generate blogs.");
        };
        reader.onerror = (error) => {
            console.error("Error reading Excel file:", error);
            alert("Failed to read Excel file. Please try again.");
        };
    };

    async function generateAnswers() {
        if (!question.trim()) return;
        setGenerating(true);
        setTyping(true);

        try {
            const apiKey = process.env.REACT_APP_GENERATIVE_LANGUAGE_CLIENT;
            if (!apiKey) throw new Error("API Key is missing. Check your .env file.");

            const formattedHistory = chatHistory.map(chat => ({
                role: "user",
                parts: [{ text: chat.question }]
            })).concat({ role: "user", parts: [{ text: question }] });

            const { data } = await axios.post(
                `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
                { contents: formattedHistory },
                { timeout: 90000 }
            );

            let responseText = data?.candidates?.[0]?.content?.parts?.[0]?.text ||
                "Hmm, I’m not sure how to respond to that, but I'm always learning! 😊";

            if (question.toLowerCase().startsWith("act as a talented blogger")) {
                responseText = responseText;
            }

            let typedResponse = "";
            for (let i = 0; i < responseText.length; i++) {
                let char = responseText[i];
                await new Promise((resolve) => setTimeout(resolve, 20));
                typedResponse += char;
                setChatHistory(prev => {
                    const newHistory = [...prev];
                    if (newHistory.length > 0 && newHistory[newHistory.length - 1].question === question) {
                        newHistory[newHistory.length - 1].response = typedResponse;
                    } else {
                        newHistory.push({ question, response: typedResponse });
                    }
                    return newHistory;
                });
            }

            setTyping(false);
            setQuestion("");
        } catch (error) {
            console.error("Error:", error);
            const errorMessage = error.response?.data?.error?.message || error.message;
            setChatHistory(prev => [
                ...prev, {
                    question,
                    response: `Oops! Something went wrong: ${errorMessage}. Let's try again in a bit. 😕`
                }]);
            setTyping(false);
            alert(`Error generating answer: ${errorMessage}`);
        } finally {
            setGenerating(false);
        }
    }

    const processSingleBlog = async (blogEntry, index = -1) => {
        let { topic, audience, keywords, wpSiteUrl, wpUsername, wpAppPassword, linkword, linkurl, category, tags, featuredImageFilename, shortblog, publishDate, publishTime } = blogEntry;
        // Sanitize category to remove markdown and extra spaces
        if (category) {
            category = category.replace(/[*_`#-]/g, '').trim();
        }

        const entryIdentifier = index !== -1 ? `Excel Row ${index + 2} - "${topic}"` : `Retrying "${topic}"`;
        const hasWpCredentials = wpSiteUrl && wpUsername && wpAppPassword;

        addBlogProcessLog(`Processing blog for ${entryIdentifier} (Excel shortblog setting: ${shortblog || 'No'})`);

        // If shortblog is "yes", only generate short blog and Word file, skip everything else
        if (shortblog && shortblog.toLowerCase() === 'yes') {
            addBlogProcessLog(`Excel specifies "yes" for short blog for "${topic}". Only generating short blog as Word file, skipping comprehensive blog and WordPress upload.`);
            try {
                const apiKey = process.env.REACT_APP_GENERATIVE_LANGUAGE_CLIENT;
                if (!apiKey) throw new Error("API Key is missing. Check your .env file.");

                // Prompt for short blog and metadata
                const shortBlogPrompt = `
Write a concise, engaging short blog (around 500 words) about "${topic}" for "${audience}". Focus on the core message and key takeaways, maintaining an engaging tone. 
Include the following internal link in the first paragraph: [${linkword}](${linkurl}).
After the blog content, generate:
Focus Keyphrase: (1 primary keyphrase for SEO)
Meta Title: (max 60 chars)
Meta Description: (max 160 chars)
Category: (single word, e.g., "Technology", "Marketing")
Tags: (comma-separated, max 15 characters each, in JSON array format like ["tag1", "tag2"])
`;

                const shortResponse = await axios.post(
                    `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
                    { contents: [{ role: "user", parts: [{ text: shortBlogPrompt }] }] },
                    { timeout: 90000 }
                );

                let shortBlogContent = shortResponse.data?.candidates?.[0]?.content?.parts?.[0]?.text;
                if (shortBlogContent) {
                    // Extract metadata from AI response
                    const focusKeyphraseMatch = shortBlogContent.match(/Focus Keyphrase:\s*(.+)/i);
                    const metaTitleMatch = shortBlogContent.match(/Meta Title:\s*(.+)/i);
                    const metaDescriptionMatch = shortBlogContent.match(/Meta Description:\s*(.+)/i);
                    const categoryMatch = shortBlogContent.match(/Category:\s*(.+)/i);
                    const tagsLineMatch = shortBlogContent.match(/Tags:\s*(.+)/i);

                    const extractedMetadata = {
                        focusKeyphrase: focusKeyphraseMatch ? focusKeyphraseMatch[1].trim() : "",
                        metaTitle: metaTitleMatch ? metaTitleMatch[1].trim() : "",
                        metaDescription: metaDescriptionMatch ? metaDescriptionMatch[1].trim() : "",
                        category: categoryMatch ? categoryMatch[1].trim() : "Uncategorized",
                        tags: []
                    };

                    if (tagsLineMatch && tagsLineMatch[1]) {
                        try {
                            let rawTagsString = tagsLineMatch[1].trim();
                            rawTagsString = rawTagsString.replace(/^\["?|"?\]$/g, '').replace(/"/g, '');
                            let parsedTags = rawTagsString.split(',').map(tag => tag.trim()).filter(tag => tag !== "");
                            if (parsedTags.length > 3) parsedTags = parsedTags.slice(0, 3);
                            extractedMetadata.tags = parsedTags;
                        } catch (e) {
                            extractedMetadata.tags = [];
                        }
                    }

                    // Remove metadata and markdown links from blog content
                    let cleanedShortBlogContent = shortBlogContent
                        .replace(/Focus Keyphrase:.+/i, '')
                        .replace(/Meta Title:.+/i, '')
                        .replace(/Meta Description:.+/i, '')
                        .replace(/Category:.+/i, '')
                        .replace(/Tags:.+/i, '')
                        .replace(/\*\*/g, "")
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1'); // Remove markdown links, keep plain text

                    cleanedShortBlogContent = humanizeText(cleanedShortBlogContent);

                    // Pass linkword and linkurl to createWordFile for clickable link in first paragraph
                    await createWordFile(`${topic}_short`, cleanedShortBlogContent, linkword, linkurl, extractedMetadata);
                    addBlogProcessLog(`Created Word file for short blog: "${topic}_short.docx".`);
                } else {
                    addBlogProcessLog(`❌ Short blog generation failed for "${topic}": AI returned no content.`);
                }
            } catch (error) {
                console.error("Error generating short blog:", error.response?.data?.message || error.message);
                addBlogProcessLog(`❌ Short blog generation failed for "${topic}": ${error.message}.`);
                return false;
            }
            // Do not upload to WordPress or generate comprehensive blog
            return true;
        }

        let comprehensiveContent = "";
        let extractedMetadata = {
            focusKeyphrase: "",
            metaTitle: "",
            metaDescription: "",
            category: "Uncategorized",
            tags: []
        };
        let finalFeaturedImageToSend = null;
        let imageSourceType = null;

        const sanitizedTopicForFilename = topic.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase();
        const imageExtensionsToTry = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];

        if (hasWpCredentials) {
            addBlogProcessLog(`Attempting Prio 1: Check backend for topic-named image with various extensions for "${sanitizedTopicForFilename}"...`);
            for (const ext of imageExtensionsToTry) {
                const potentialFilenameWithExt = `${sanitizedTopicForFilename}${ext}`;
                const topicImageBackendUrl = `${BACKEND_URL}/images/${encodeURIComponent(potentialFilenameWithExt)}`;
                try {
                    const imageCheckResponse = await axios.head(topicImageBackendUrl);
                    if (imageCheckResponse.status === 200) {
                        finalFeaturedImageToSend = topicImageBackendUrl;
                        imageSourceType = 'url';
                        addBlogProcessLog(`✅ Prio 1: Found topic-named image on backend: ${topicImageBackendUrl}`);
                        break;
                    } else {
                        addBlogProcessLog(`Prio 1: Topic-named image "${potentialFilenameWithExt}" not found on backend (${imageCheckResponse.status}).`);
                    }
                } catch (error) {
                    addBlogProcessLog(`Error checking Prio 1 topic-named image "${potentialFilenameWithExt}" on backend: ${error.message}.`);
                }
            }
        } else {
            addBlogProcessLog(`Skipping image checks for WordPress because credentials are missing.`);
        }

        if (!finalFeaturedImageToSend && featuredImageFilename && uploadedImages.length > 0) {
            addBlogProcessLog(`Attempting Prio 2: Check frontend-uploaded images for Excel-specified filename "${featuredImageFilename}"...`);
            const matchedFrontendImage = uploadedImages.find(
                (img) => img.name.toLowerCase() === featuredImageFilename.toLowerCase()
            );

            if (matchedFrontendImage) {
                finalFeaturedImageToSend = matchedFrontendImage;
                imageSourceType = 'file';
                addBlogProcessLog(`✅ Prio 2: Found matching frontend-uploaded image: ${matchedFrontendImage.name}`);
            } else {
                addBlogProcessLog(`Prio 2: Frontend-uploaded image not found for Excel-specified filename "${featuredImageFilename}".`);
            }
        }

        if (!finalFeaturedImageToSend && hasWpCredentials) {
            addBlogProcessLog(`No featured image found for ${entryIdentifier} after all priority checks. Blog will be uploaded without a featured image.`);
        }

        try {
            const apiKey = process.env.REACT_APP_GENERATIVE_LANGUAGE_CLIENT;
            if (!apiKey) throw new Error("API Key is missing. Check your .env file.");

            addBlogProcessLog(`Sending request for comprehensive blog for ${entryIdentifier}...`);
    const comprehensivePrompt = `Act as a talented blogger passionate about creating valuable, engaging content that demonstrates E-E-A-T. Craft a comprehensive blog post (around 1000 words) about "${topic}" aimed at "${audience}". Use a conversational, second-person tone combined with a neutral/objective perspective. Employ vivid imagery, storytelling techniques, and personal anecdotes to connect with readers on an emotional level. Structure the post into several sections, each with imaginative titles and at least two paragraphs of insightful content. Optimize the "${keywords}" naturally throughout the text, avoiding keyword stuffing. Incorporate thought-provoking questions related to the topic as section titles, followed by informative and engaging responses. After the post, create a compelling Focus Keyphrase (1 primary keyphrase for SEO), Meta Title (max 60 chars), Meta Description (max 160 chars), Category (single word, e.g., "Technology", "Marketing") and Tags (comma-separated, max 15 characters each, in JSON array format like ["tag1", "tag2"]). Ensure the blog content is clearly separated from the metadata.`;

            const comprehensiveResponse = await axios.post(
                `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
                { contents: [{ role: "user", parts: [{ text: comprehensivePrompt }] }] },
                { timeout: 90000 }
            );

            comprehensiveContent = comprehensiveResponse.data?.candidates?.[0]?.content?.parts?.[0]?.text || "Blog content not available.";

            const focusKeyphraseMatch = comprehensiveContent.match(/Focus Keyphrase:\s*(.+)/i);
            const metaTitleMatch = comprehensiveContent.match(/Meta Title:\s*(.+)/i);
            const metaDescriptionMatch = comprehensiveContent.match(/Meta Description:\s*(.+)/i);
            const categoryMatch = comprehensiveContent.match(/Category:\s*(.+)/i);
            const tagsLineMatch = comprehensiveContent.match(/Tags:\s*(.+)/i);

            extractedMetadata.focusKeyphrase = focusKeyphraseMatch ? focusKeyphraseMatch[1].trim() : "";
            extractedMetadata.metaTitle = metaTitleMatch ? metaTitleMatch[1].trim() : "Default Meta Title";
            extractedMetadata.metaDescription = metaDescriptionMatch ? metaDescriptionMatch[1].trim() : "Default Meta Description";
            extractedMetadata.category = categoryMatch ? categoryMatch[1].trim() : "Uncategorized";

            if (tagsLineMatch && tagsLineMatch[1]) {
                try {
                    let rawTagsString = tagsLineMatch[1].trim();
                    rawTagsString = rawTagsString.replace(/^\["?|"?\]$/g, '').replace(/"/g, '');

                    let parsedTags = rawTagsString.split(',').map(tag => tag.trim()).filter(tag => tag !== "");

                    if (parsedTags.length > 3) {
                        addBlogProcessLog(`⚠️ More than 3 tags generated for "${topic}". Truncating to first 3 tags.`);
                        parsedTags = parsedTags.slice(0, 3);
                    }
                    extractedMetadata.tags = parsedTags;
                } catch (e) {
                    console.error("Error parsing tags from AI response:", e);
                    addBlogProcessLog(`❌ Error parsing tags for "${topic}": ${e.message}. Using empty array.`);
                    extractedMetadata.tags = [];
                }
            } else {
                extractedMetadata.tags = [];
            }

            let contentForWordPress = comprehensiveContent;
            contentForWordPress = contentForWordPress.replace(/Focus Keyphrase:.+/i, '');
            contentForWordPress = contentForWordPress.replace(/Meta Title:.+/i, '');
            contentForWordPress = contentForWordPress.replace(/Meta Description:.+/i, '');
            contentForWordPress = contentForWordPress.replace(/Category:.+/i, '');
            contentForWordPress = contentForWordPress.replace(/Tags:.+/i, '');
            contentForWordPress = contentForWordPress.replace(/\*\*/g, "");

            if (linkword && linkurl) {
                const linkHtml = `<a href="${linkurl}" target="_blank" rel="noopener">${linkword}</a>`;
                const firstParagraphRegex = /(<p[^>]*>)/i;
                if (contentForWordPress.match(firstParagraphRegex)) {
                    contentForWordPress = contentForWordPress.replace(firstParagraphRegex, `$1${linkHtml} `);
                    addBlogProcessLog("Internal link inserted into the first paragraph.");
                } else {
                    addBlogProcessLog("Warning: No paragraph found to insert internal link. Prepending to content.");
                    contentForWordPress = `${linkHtml} ${contentForWordPress}`;
                }
            }
            contentForWordPress = humanizeText(contentForWordPress);
            addBlogProcessLog(`Comprehensive blog content cleaned, linked, and humanized for ${entryIdentifier}.`);

            const cleanedComprehensiveContentForWord = comprehensiveContent.replace(/\*\*/g, "");
            createWordFile(topic, cleanedComprehensiveContentForWord, linkword, linkurl, extractedMetadata);
            addBlogProcessLog(`Created Word file for comprehensive blog: "${topic}.docx".`);

            let shortBlogContent = "";
            let shouldGenerateShortBlog = false;
            if (shortblog && shortblog.toLowerCase() === 'yes') {
                shouldGenerateShortBlog = window.confirm(`Excel specifies "yes" for short blog for "${topic}". Do you want to generate a short blog?`);

                if (shouldGenerateShortBlog) {
                    addBlogProcessLog(`User confirmed short blog generation for "${topic}". Calling AI for short version...`);
                    try {
                        const shortBlogPrompt = `Summarize the following comprehensive blog post into a concise, engaging short blog (around 700 words). Focus on the core message and key takeaways, maintaining an engaging tone. Do not include any metadata like Focus Keyphrase, Meta Title, etc. Just the blog content. \n\nBlog Content:\n${comprehensiveContent}`;
                        const shortResponse = await axios.post(
                            `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
                            { contents: [{ role: "user", parts: [{ text: shortBlogPrompt }] }] },
                            { timeout: 90000 }
                        );

                        shortBlogContent = shortResponse.data?.candidates?.[0]?.content?.parts?.[0]?.text;
                        if (shortBlogContent) {
                            const cleanedShortBlogContent = shortBlogContent.replace(/\*\*/g, "");
                            const humanizedShortBlog = humanizeText(cleanedShortBlogContent);
                            addBlogProcessLog(`Short blog content generated and humanized for "${topic}".`);
                            createWordFile(`${topic}_short`, humanizedShortBlog, linkword, linkurl, extractedMetadata);
                            addBlogProcessLog(`Created Word file for short blog: "${topic}_short.docx".`);
                        } else {
                            addBlogProcessLog(`❌ Short blog generation failed for "${topic}": AI returned no content.`);
                        }

                    } catch (error) {
                        console.error("Error generating short blog:", error.response?.data?.message || error.message);
                        addBlogProcessLog(`❌ Short blog generation failed for "${topic}": ${error.message}.`);
                    }
                } else {
                    addBlogProcessLog(`User cancelled short blog generation for "${topic}".`);
                }
            } else {
                addBlogProcessLog(`Excel specifies "${shortblog || 'No'}" for short blog for ${entryIdentifier}. No short version will be generated.`);
            }

            if (hasWpCredentials) {
                addBlogProcessLog(`Preparing data for WordPress upload for ${entryIdentifier}...`);
                const formData = new FormData();
                formData.append('title', topic);
                formData.append('content', contentForWordPress);
                formData.append('focusKeyphrase', extractedMetadata.focusKeyphrase);
                formData.append('metaTitle', extractedMetadata.metaTitle);
                formData.append('metaDescription', extractedMetadata.metaDescription);
                formData.append('category', extractedMetadata.category);
                formData.append('tags', JSON.stringify(extractedMetadata.tags));
                formData.append('wpSiteUrl', wpSiteUrl);
                formData.append('wpUsername', wpUsername);
                formData.append('wpAppPassword', wpAppPassword);
                formData.append('linkword', linkword);
                formData.append('linkurl', linkurl);
                
                // Add new fields for scheduling
                formData.append('publishDate', publishDate || '');
                formData.append('publishTime', publishTime || '');

                if (finalFeaturedImageToSend) {
                    if (imageSourceType === 'url') {
                        formData.append('featuredImageUrl', finalFeaturedImageToSend);
                        addBlogProcessLog(`Sending featured image URL to backend: ${finalFeaturedImageToSend}`);
                    } else if (imageSourceType === 'file') {
                        formData.append('featuredImage', finalFeaturedImageToSend);
                        addBlogProcessLog(`Sending frontend-uploaded featured image to backend: ${finalFeaturedImageToSend.name}`);
                    }
                } else {
                    addBlogProcessLog(`No featured image will be sent to backend for ${entryIdentifier}.`);
                }

                addBlogProcessLog(`Uploading blog "${topic}" to WordPress for ${entryIdentifier}...`);
                await uploadToWordPress(formData);
                addBlogProcessLog(`✅ Successfully processed and uploaded ${entryIdentifier}.`);
            } else {
                addBlogProcessLog(`Skipping WordPress upload for ${entryIdentifier} due to missing credentials.`);
            }

            return true;

        } catch (error) {
            console.error(`Error processing blog for ${entryIdentifier}:`, error);
            const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || error.message;
            addBlogProcessLog(`❌ Failed to process blog for ${entryIdentifier}. Error: ${errorMessage}.`);
            return false;
        }
    };


    const generateBlogs = async () => {
        if (blogData.length === 0) {
            alert("No blog data available! Please upload an Excel file first.");
            return;
        }

        setGenerating(true);
        setTyping(true);
        setBlogProcessLogs([]);
        setFailedBlogs([]);

        const currentFailedBlogs = [];

        for (let i = 0; i < blogData.length; i++) {
            const blogEntry = blogData[i];
            const success = await processSingleBlog(blogEntry, i);

            if (!success) {
                currentFailedBlogs.push(blogEntry);
            }

            addBlogProcessLog(`Finished processing "${blogEntry.topic}". Waiting 5 seconds before next blog.`);
            await sleep(5000);
        }

        setGenerating(false);
        setTyping(false);
        setFailedBlogs(currentFailedBlogs);
        addBlogProcessLog("All initial batch blog processing finished.");

        if (currentFailedBlogs.length > 0) {
            alert(`Batch completed with ${currentFailedBlogs.length} failures. You can now attempt retries.`);
        } else {
            alert("All blogs processed successfully!");
        }
    };

    const handleFailedBlogRetries = async (blogsToRetry = failedBlogs) => {
        if (blogsToRetry.length === 0) {
            addBlogProcessLog("No failed blogs to retry.");
            return;
        }

        addBlogProcessLog(`Attempting retries for ${blogsToRetry.length} failed blog(s)...`);
        setGenerating(true);
        setTyping(true);

        const successfullyRetried = [];
        const stillFailed = [];

        for (const blog of blogsToRetry) {
            const confirmRetry = window.confirm(`Can try to regenerate and upload "${blog.topic}" on WordPress site...?`);

            if (confirmRetry) {
                addBlogProcessLog(`User confirmed retry for "${blog.topic}".`);
                const success = await processSingleBlog(blog);
                if (success) {
                    successfullyRetried.push(blog);
                } else {
                    stillFailed.push(blog);
                }
                addBlogProcessLog(`Finished retry attempt for "${blog.topic}". Waiting 5 seconds before next retry prompt.`);
                await sleep(5000);
            } else {
                addBlogProcessLog(`User skipped retry for "${blog.topic}".`);
                stillFailed.push(blog);
            }
        }

        setFailedBlogs(stillFailed);
        setGenerating(false);
        setTyping(false);

        if (stillFailed.length === 0) {
            addBlogProcessLog("All previously failed blogs have been successfully processed or skipped.");
            alert("All retries completed successfully!");
        } else {
            addBlogProcessLog(`${successfullyRetried.length} blogs successfully retried. ${stillFailed.length} blogs still failed/skipped.`);
            alert(`Retries completed. ${stillFailed.length} blogs still need attention.`);
        }
    };


    const createWordFile = async (fileName, content, linkword, linkurl, metadata) => {
        const docChildren = [];

        // Remove the first metadata block from content before splitting into paragraphs
        // This matches and removes the block starting with Focus Keyphrase: ... up to the end of the tags line (array or plain)
        let cleanedContent = content.replace(
            /Focus Keyphrase:[^\n\r]*[\n\r]+Meta Title:[^\n\r]*[\n\r]+Meta Description:[^\n\r]*[\n\r]+Category:[^\n\r]*[\n\r]+Tags:[^\n\r]*(\[[^\]]*\])?[\n\r]*/i,
            ''
        );

        // Add the main title
        docChildren.push(
            new Paragraph({
                text: fileName,
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 300 },
                alignment: AlignmentType.CENTER,
            })
        );

        // Process content to create paragraphs
        const contentParagraphs = cleanedContent.split("\n\n").filter(p => p.trim() !== "");
        let firstParagraphProcessed = false;

        contentParagraphs.forEach((paragraphText, idx) => {
            const children = [];
            const lines = paragraphText.split('\n');

            // For short blog, insert link at a random position in first paragraph
            if (!firstParagraphProcessed && idx === 0 && linkword && linkurl) {
                // Merge all lines of first paragraph
                const paragraphTextJoined = lines.join(' ').trim();
                const words = paragraphTextJoined.split(' ');
                if (words.length > 0) {
                    // Pick a random index in the words array
                    const randomIndex = Math.floor(Math.random() * words.length);
                    // Insert the link at the random position
                    const before = words.slice(0, randomIndex).join(' ');
                    const after = words.slice(randomIndex).join(' ');
                    if (before) children.push(new TextRun(before + ' '));
                    children.push(new ExternalHyperlink({
                        link: linkurl,
                        children: [new TextRun({ text: linkword, style: "Hyperlink" })],
                    }));
                    if (after) children.push(new TextRun(' ' + after));
                }
            } else {
                // ...existing code for headings and normal lines...
                lines.forEach((line, lineIdx) => {
                    if (line.trim() === "") return;

                    let headingLevel = null;
                    if (line.startsWith("###### ")) { headingLevel = HeadingLevel.HEADING_6; }
                    else if (line.startsWith("##### ")) { headingLevel = HeadingLevel.HEADING_5; }
                    else if (line.startsWith("#### ")) { headingLevel = HeadingLevel.HEADING_4; }
                    else if (line.startsWith("### ")) { headingLevel = HeadingLevel.HEADING_3; }
                    else if (line.startsWith("## ")) { headingLevel = HeadingLevel.HEADING_2; }
                    else if (line.startsWith("# ")) { headingLevel = HeadingLevel.HEADING_1; }

                    if (headingLevel) {
                        docChildren.push(new Paragraph({
                            text: line.replace(/#+\s/, "").trim(),
                            heading: headingLevel,
                            spacing: { after: 150 },
                            alignment: AlignmentType.LEFT,
                        }));
                    } else {
                        children.push(new TextRun(line.trim()));
                    }
                });
            }

            if (children.length > 0) {
                docChildren.push(new Paragraph({
                    children: children,
                    spacing: { line: 276 },
                    alignment: AlignmentType.JUSTIFIED,
                }));
            }
            firstParagraphProcessed = true;
        });

        // Only add the "--- Blog Metadata ---" block, remove any earlier metadata
        if (metadata) {
            const clean = (val) => typeof val === 'string' ? val.replace(/[\*\[]/g, '').trim() : val;
            const cleanTags = Array.isArray(metadata.tags) ? metadata.tags.map(tag => clean(tag)) : [];

            docChildren.push(
                new Paragraph({
                    text: "",
                    spacing: { after: 300 }
                })
            );
            docChildren.push(
                new Paragraph({
                    text: "--- Blog Metadata ---",
                    heading: HeadingLevel.HEADING_2,
                    alignment: AlignmentType.CENTER
                })
            );
            docChildren.push(
                new Paragraph({
                    text: `Focus Keyphrase: ${clean(metadata.focusKeyphrase)}`,
                    spacing: { after: 100 }
                })
            );
            docChildren.push(
                new Paragraph({
                    text: `Meta Title: ${clean(metadata.metaTitle)}`,
                    spacing: { after: 100 }
                })
            );
            docChildren.push(
                new Paragraph({
                    text: `Meta Description: ${clean(metadata.metaDescription)}`,
                    spacing: { after: 100 }
                })
            );
            docChildren.push(
                new Paragraph({
                    text: `Category: ${clean(metadata.category)}`,
                    spacing: { after: 100 }
                })
            );
            docChildren.push(
                new Paragraph({
                    text: `Tags: ${cleanTags.join(', ')}`,
                    spacing: { after: 100 }
                })
            );
        }

        const doc = new Document({ sections: [{ children: docChildren }] });

        try {
            const blob = await Packer.toBlob(doc);
            saveAs(blob, `${fileName.replace(/\s+/g, "_")}.docx`);
            addBlogProcessLog(`Successfully created Word file: "${fileName.replace(/\s+/g, "_")}.docx"`);
        } catch (error) {
            console.error("Error creating Word file:", error);
            addBlogProcessLog(`❌ Error creating Word file for "${fileName}": ${error.message}`);
        }
    };


    const uploadToWordPress = async (formData) => {
        addBlogProcessLog("Sending blog data to backend for WordPress upload...");

        try {
            const response = await axios.post(
                `${BACKEND_URL}/api/blog/upload-blog`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 120000
                }
            );

            if (response.data && response.data.link) {
                addBlogProcessLog(`✅ Blog uploaded successfully to WordPress. Permalink: ${response.data.link}`);
                window.open(response.data.link, "_blank");
            } else {
                addBlogProcessLog("❌ WordPress upload successful, but no URL returned from backend. Check backend logs.");
            }
            return response.data;
        } catch (error) {
            console.error("❌ Failed to upload blog to WordPress:", error);
            const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || error.message;
            addBlogProcessLog(`❌ WordPress Upload Error: ${errorMessage}. Check backend server logs for details.`);
            throw new Error(`WordPress Upload Failed: ${errorMessage}`);
        }
    };

    return (
        <div
            className={`min-h-screen flex flex-col p-0 ${theme === "dark" ? "" : "bg-cover bg-center"}`}
            style={
                theme !== "dark"
                    ? {
                        backgroundImage: `url()`,
                    }
                    : {}
            }
        >
            <div className="flex flex-col items-center w-full">
                <div className="flex flex-col items-center text-center">
                    <h1 className="font-bold text-4xl sm:text-5xl">
                        <span className="text-blue-600">One</span>
                        <span className="text-red-600">H</span>
                        <span className="text-yellow-500">ub</span>
                        <span className="text-lg text-gray-800">'s Chat</span>
                        <span className="text-lg text-green-600">bot</span>
                    </h1>
                </div>

                <p className="text-green-600 ">
                    Make <span className="text-orange-500">Tamil Nadu </span>proud with us.

                </p>

                <div className="w-1/2 shadow-lg rounded-lg overflow-hidden mt-4">
                    <div className="bg-gray-100 overflow-auto p-4" style={{ height: "300px" }}>
                        {chatHistory.map((chat, index) => (
                            <div key={`chat-${index}`} className="mb-3">
                                <p className="text-right bg-white p-2 rounded shadow-sm">{chat.question}</p>
                                <p className="text-left bg-white p-2 rounded shadow-sm">
                                    <ReactMarkdown>{chat.response}</ReactMarkdown>
                                </p>
                            </div>
                        ))}
                        {blogProcessLogs.map((log, index) => (
                            <p key={`log-${index}`} className="text-gray-700 text-sm mt-1">
                                {log}
                            </p>
                        ))}
                        {typing && <p className="text-gray-500 italic">Bot is typing or processing...</p>}
                        <div ref={chatEndRef} />
                        <div ref={blogProcessLogsEndRef} />
                    </div>

                    <div className="bg-white mt-2">
                        <b>
                            <span style={{ color: 'orange', textDecoration: 'underline' }}>Priority 1</span> automatically searches for an image in the backend's public/images folder. It uses a sanitized version of the blog's topic to find the corresponding image file.
                        </b>

                        <b><span style={{ color: 'orange', textDecoration: 'underline' }}>Priority 2</span> is a fallback. If Priority 1 fails, the system checks for a file with the exact name you specified in the Excel sheet's featuredImage column among the images you manually uploaded via the frontend.</b>
                    </div>
                    <br></br>
                    <a
                        href="/sample-excel-single.xlsx"
                        download="sample-excel-single.xlsx"
                          className="absolute top-4 right-4 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-4 rounded-lg shadow text-sm transition-colors duration-200"
                    >
                        Download Sample Excel Sheet
                    </a>
                </div>

                <div className="mt-3 w-1/2 flex">
                    <input
                        type="file"
                        className="flex-grow border border-gray-300 rounded px-3 py-2"
                        accept=".xlsx, .xls"
                        onChange={handleExcelFileChange}
                    />
                    <button
                        className="ml-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                        onClick={handleCancelUpload}
                        disabled={(!excelFile && uploadedImages.length === 0 && blogData.length === 0 && failedBlogs.length === 0)}
                    >
                        Cancel All Uploads
                    </button>
                    <button
                        className="ml-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        onClick={handleFileUpload}
                        disabled={!excelFile || generating}
                    >
                        Upload Excel
                    </button>
                </div>

                <div className="mt-2 w-1/2 flex items-center">
                    <input
                        type="file"
                        multiple
                        className="flex-grow border border-gray-300 rounded px-3 py-2"
                        accept="image/*"
                        onChange={handleImageBatchChange}
                    />
                    {uploadedImages.length > 0 && (
                        <span className="ml-2 text-sm text-gray-600">
                            {uploadedImages.length} image(s) selected
                        </span>
                    )}
                </div>

                <button
                    className="bg-blue-600 text-white mt-3 w-1/2 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                    onClick={generateBlogs}
                    disabled={generating || blogData.length === 0}
                >
                    {generating ? "Generating Blogs..." : "Generate Blog(s)"}
                </button>

                {failedBlogs.length > 0 && (
                    <div className="mt-4 w-1/2 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
                        <h3 className="font-bold text-lg mb-2">Failed Blogs ({failedBlogs.length})</h3>
                        <ul className="list-disc list-inside">
                            {failedBlogs.map((blog, index) => (
                                <li key={`failed-${index}`}>
                                    {blog.topic}
                                </li>
                            ))}
                        </ul>
                        <button
                            className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 mt-3 disabled:opacity-50"
                            onClick={() => handleFailedBlogRetries()}
                            disabled={generating}
                        >
                            {generating ? "Retrying..." : "Attempt Retries for Failed Blogs"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Generator;